/**
 * The multi-layer magic circle that forms in front of the caster.
 *
 * Three stacked rune bands at different depths, counter-rotating, plus an
 * orbit ring and a shockwave that fires on a successful parry. The layering is
 * deliberate: a single flat ring reads as a decal, three offset ones read as a
 * structure hanging in the air.
 */
import { useMemo, useRef } from 'react';
import * as THREE from 'three';
import { useFrame } from '@react-three/fiber';
import { useCameraSpace } from '../camera/useCameraSpace';
import { scene as bus } from '../scene/sceneBus';
import { glowTexture, runeRingTexture } from '../assets/textures';
import { CAST_RIG } from './castRig';

/** Reused every frame; `Vector3.lerp` writes into the receiver, not this. */
const targetScaleVec = new THREE.Vector3();

export function MagicCircle() {
  const anchor = useRef<THREE.Group>(null);
  const rig = useRef<THREE.Group>(null);
  useCameraSpace(anchor);

  const layers = useRef<THREE.Mesh[]>([]);
  const orbit = useRef<THREE.Mesh>(null);
  const core = useRef<THREE.Sprite>(null);
  const shock = useRef<THREE.Mesh>(null);
  const light = useRef<THREE.PointLight>(null);
  const shockT = useRef(-1);
  const lastBurst = useRef(0);

  const runeA = useMemo(() => runeRingTexture(1), []);
  const runeB = useMemo(() => runeRingTexture(3), []);
  const runeC = useMemo(() => runeRingTexture(5), []);
  const glow = useMemo(() => glowTexture(), []);
  const color = useMemo(() => new THREE.Color('#8fd8ff'), []);

  useFrame((state, delta) => {
    const group = rig.current;
    if (!group) return;

    // Placement is shared with the sigil traced onto it — see castRig.ts for
    // why it sits where it does.
    group.position.set(CAST_RIG.x, CAST_RIG.y, CAST_RIG.z);

    const c = bus.circle;
    const t = state.clock.elapsedTime;
    color.setStyle(c.color);

    const targetScale = c.active
      ? CAST_RIG.scaleMin + c.progress * (CAST_RIG.scaleMax - CAST_RIG.scaleMin)
      : 0.001;
    group.scale.lerp(targetScaleVec.setScalar(targetScale), Math.min(1, delta * 11));
    group.visible = group.scale.x > 0.02;

    const alpha = c.active ? 0.35 + c.progress * 0.65 : Math.max(0, group.scale.x);

    layers.current.forEach((mesh, i) => {
      if (!mesh) return;
      const dir = i % 2 === 0 ? 1 : -1;
      mesh.rotation.z = t * (0.35 + i * 0.28) * dir;
      mesh.position.z = -i * 0.055;
      mesh.scale.setScalar(1 - i * 0.16);
      const mat = mesh.material as THREE.MeshBasicMaterial;
      mat.color.copy(color);
      const visibleLayer = i < c.complexity;
      mat.opacity = visibleLayer ? alpha * (0.9 - i * 0.16) : 0;
    });

    if (orbit.current) {
      orbit.current.rotation.z = -t * 1.6;
      orbit.current.rotation.x = Math.sin(t * 0.8) * 0.35;
      const mat = orbit.current.material as THREE.MeshBasicMaterial;
      mat.color.copy(color);
      mat.opacity = alpha * 0.7;
    }

    if (core.current) {
      const mat = core.current.material as THREE.SpriteMaterial;
      mat.color.copy(color);
      mat.opacity = alpha * 0.85;
      // Trimmed again as the rig grew: the core scales with the rig, so the
      // factor has to come down every time the rig goes up or the bloom creeps
      // back over the reticle. At `scaleMax` this keeps its top edge just under
      // the crosshair.
      core.current.scale.setScalar(0.17 + c.progress * 0.24 + Math.sin(t * 9) * 0.025);
    }

    if (light.current) {
      light.current.color.copy(color);
      light.current.intensity = alpha * 5.5;
    }

    // Parry shockwave.
    if (c.burstAt > lastBurst.current) {
      lastBurst.current = c.burstAt;
      shockT.current = 0;
    }
    if (shock.current) {
      if (shockT.current >= 0) {
        shockT.current += delta;
        const p = shockT.current / 0.55;
        if (p >= 1) {
          shockT.current = -1;
          shock.current.visible = false;
        } else {
          shock.current.visible = true;
          const s = 0.6 + p * 5.5;
          shock.current.scale.set(s, s, s);
          const mat = shock.current.material as THREE.MeshBasicMaterial;
          mat.color.copy(color);
          mat.opacity = (1 - p) * 0.85;
        }
      } else {
        shock.current.visible = false;
      }
    }
  });

  const layerTextures = [runeA, runeB, runeC];

  return (
    <group ref={anchor}>
      <group ref={rig} visible={false}>
      {layerTextures.map((tex, i) => (
        <mesh
          key={i}
          ref={(el) => {
            if (el) layers.current[i] = el;
          }}
        >
          <planeGeometry args={[1.5, 1.5]} />
          <meshBasicMaterial
            map={tex}
            transparent
            opacity={0}
            blending={THREE.AdditiveBlending}
            depthWrite={false}
            depthTest={false}
            side={THREE.DoubleSide}
            toneMapped={false}
          />
        </mesh>
      ))}

      {/* Orbit ring, tilted out of plane to give the circle volume. */}
      <mesh ref={orbit}>
        <torusGeometry args={[0.82, 0.008, 6, 64]} />
        <meshBasicMaterial
          transparent
          opacity={0}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
          depthTest={false}
          toneMapped={false}
        />
      </mesh>

      <sprite ref={core} scale={[0.4, 0.4, 0.4]}>
        <spriteMaterial
          map={glow}
          transparent
          opacity={0}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
          depthTest={false}
          toneMapped={false}
        />
      </sprite>

      {/* Shockwave on a successful parry. */}
      <mesh ref={shock} visible={false}>
        <ringGeometry args={[0.55, 0.72, 48]} />
        <meshBasicMaterial
          transparent
          opacity={0}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
          depthTest={false}
          side={THREE.DoubleSide}
          toneMapped={false}
        />
      </mesh>

        <pointLight ref={light} intensity={0} distance={9} decay={2} />
      </group>
    </group>
  );
}
